
import { Link } from 'react-router-dom';

export default function EquityPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-scales-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">公平性理論</h1>
            <p className="text-xl text-white/90">Equity Theory</p>
            <p className="text-lg text-white/80 mt-2">提唱者：Adams</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-emerald-500 to-green-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">人は「自分と他者の投入／報酬の比率」を無意識に比較する</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 理論の概要 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-emerald-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                理論の概要
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                公平性理論によると、人は自分の「投入（努力・時間・スキル）」と「報酬（給与・評価・承認）」の比率を、他者と比較します。この比率が不公平だと感じると、モチベーションが低下します。
              </p>
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-emerald-100">
                    <div className="text-2xl font-bold text-emerald-600 mb-2">自分の投入</div>
                    <div className="text-gray-600 text-sm">努力・時間・スキル</div>
                  </div>
                  <div className="flex items-center justify-center">
                    <i className="ri-arrow-left-right-line text-3xl text-emerald-500"></i>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-emerald-100">
                    <div className="text-2xl font-bold text-emerald-600 mb-2">自分の報酬</div>
                    <div className="text-gray-600 text-sm">給与・評価・承認</div>
                  </div>
                </div>
                <div className="text-center mt-4 text-gray-700">
                  この比率を<strong className="text-emerald-600">他者と比較</strong>して公平性を判断
                </div>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-emerald-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-eye-line text-emerald-600 mr-2"></i>
                    不公平感・静かな不満の可視化
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    職員が口に出さなくても、不公平感を感じている可能性があります。以下のサインに注意しましょう。
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-emerald-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">不公平感のサイン</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・「あの人ばかり評価される」</li>
                        <li>・「自分だけ仕事が多い」</li>
                        <li>・モチベーションの急な低下</li>
                        <li>・協力的でなくなる</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-green-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">確認の問いかけ</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・「業務量は適切ですか？」</li>
                        <li>・「評価に納得していますか？」</li>
                        <li>・「不公平に感じることは？」</li>
                        <li>・「改善してほしいことは？」</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-file-text-line text-green-600 mr-2"></i>
                    評価・役割分担の説明責任設計
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    不公平感を防ぐには、評価基準と役割分担の<strong className="text-green-600">透明性</strong>が重要です。
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start p-3 bg-white rounded-lg border border-green-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">評価基準を明文化し、全員に共有する</span>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-green-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">役割分担の理由を説明する</span>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-green-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">評価結果のフィードバックを丁寧に行う</span>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-green-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">不満があれば聞く機会を設ける</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                職員が不公平感を感じていないか、さりげなく確認してみましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  1on1で「業務量や評価について、不公平に感じることはありますか？」と問いかけてみる。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation/behavioral-economics" className="flex items-center text-emerald-600 hover:text-emerald-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                行動経済学
              </Link>
              <Link to="/motivation/burnout" className="flex items-center text-emerald-600 hover:text-emerald-700 font-semibold transition-colors">
                バーンアウト理論
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
